import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { GlobalConfiguration, MetaTag, TagSampleValue, Template, TemplateConfiguration} from "@/models/interface"
import { BaseTemplateTags, DEFAULT_SUPPORT_EMAIL, PreferredLanguage } from "@/models/enum"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const containsHtmlTag = (input: string): boolean => {
  const htmlTagRegex = /<[^>]+>/;
  return htmlTagRegex.test(input);
}

/**
 * Gets sample value for a specific language from TagSampleValue array
 * @param sampleValues - Array of TagSampleValue objects
 * @param language - Supported language ("en" or "fr")
 * @param includeHtml - If false, reset the tag value to empty string
 * @returns The sample value for the specified language, or English as fallback, or empty string if none found
 */
export function getSampleValueByLanguage(sampleValues: TagSampleValue[], 
  language: PreferredLanguage, includeHtml: boolean = true): string {
  // First try to find the requested language
  const requestedLang = sampleValues?.find(sv => sv.Language === language)
  if (requestedLang) {
    return includeHtml ? requestedLang.Value : !containsHtmlTag(requestedLang.Value) ? requestedLang.Value : ''
  }
  
  // Fallback to English if requested language not found
  const englishLang = sampleValues.find(sv => sv.Language === "en") 
  if (englishLang) {
    return includeHtml ? englishLang.Value : !containsHtmlTag(englishLang.Value) ? englishLang.Value : ''
  }
  return ""
}

/**
 * Extracts and validates meta tags from template content
 * @param subject - The template subject string
 * @param content - The template content/body string
 * @param allowedMetaTags - Array of allowed meta tags from configuration
 * @param template - Selected template to get the correct values
 * @returns Object containing valid MetaTag objects and invalid tag names found in the content
 */
export function extractAndValidateMetaTags(
  subject: string,
  content: string,
  allowedMetaTags: MetaTag[],
  template: GlobalConfiguration<Template>
): {
  validMetaTags: MetaTag[]
  invalidTags: string[]
} {
  // Combine subject and content for tag extraction
  const combinedText = `${subject} ${content}`
  
  // Regex to find all {{Tag_Name}} patterns
  const tagPattern = /\{\{([^}]+)\}\}/g
  const foundTags = new Set<string>()
  
  // Extract all unique tag names
  let match
  while ((match = tagPattern.exec(combinedText)) !== null) {
    foundTags.add(match[1])
  }
  
  // System tags that should be ignored during validation
  const systemTags = new Set(['CtaUrl'])
  
  // Validate against allowed meta tags and separate valid from invalid
  const validMetaTags: MetaTag[] = []
  const invalidTags: string[] = []
  
  foundTags.forEach(tagName => {
    // Skip system tags
    if (systemTags.has(tagName)) {
      return
    }
    
    // First check if the tag is allowed by checking against allowedMetaTags
    let allowedTag = allowedMetaTags.find(
      metaTag => metaTag.Key === tagName || metaTag.Value === tagName
    )
    
    // If not found in main tags, check sub meta tags
    if (!allowedTag) {
      for (const metaTag of allowedMetaTags) {
        if (metaTag?.SubMetaTags && metaTag?.SubMetaTags?.length > 0) {
          const subTag = metaTag.SubMetaTags.find(
            sub => sub.Key === tagName || sub.Value === tagName
          )
          if (subTag) {
            allowedTag = subTag
            break
          }
        }
      }
    }
    
    if (allowedTag) {
      // Now find the original MetaTag from template.Data.MetaTag.SelectedMetaTags
      let originalTag = template.Data.MetaTag.SelectedMetaTags.find(
        metaTag => metaTag.Key === tagName || metaTag.Value === tagName
      )
      
      // If not found in main selected tags, check sub meta tags
      if (!originalTag) {
        for (const metaTag of template.Data.MetaTag.SelectedMetaTags) {
          if (metaTag?.SubMetaTags && metaTag?.SubMetaTags?.length > 0) {
            const subTag = metaTag.SubMetaTags.find(
              sub => sub.Key === tagName || sub.Value === tagName
            )
            if (subTag) {
              originalTag = subTag
              break
            }
          }
        }
      }
      
      // Use the original tag if found, otherwise fall back to allowed tag
      const tagToAdd = originalTag || allowedTag
      
      // Avoid duplicates
      const alreadyAdded = validMetaTags.some(
        tag => tag.Key === tagToAdd.Key
      )
      
      if (!alreadyAdded) {
        validMetaTags.push(tagToAdd)
      }
    } else {
      // Track invalid tags for error reporting
      invalidTags.push(tagName)
    }
  })
  
  return {
    validMetaTags,
    invalidTags
  }
}

/**
 * Processes template content by replacing meta tags with their appropriate values
 * @param template
 * @param subject - The template subject string
 * @param content - The template content/body string
 * @param withSampleData - If true, use SampleValue; if false, use Value
 * @param language - The preferred language for sample values
 * @returns Object with processed subject and content
 */
export function processTemplateContent(
  template: TemplateConfiguration,
  subject: string,
  content: string,
  withSampleData: boolean,
  language: PreferredLanguage = PreferredLanguage.English
): { subject: string; content: string } {
  let processedSubject = subject
  let processedContent = content

  // Process all meta tags from all configurations
    template.Data.MetaTag.SelectedMetaTags.forEach(metaTag => {
      const tagPattern = new RegExp(`\\{\\{${metaTag.Key}\\}\\}`, 'g')
      const replacementValue = withSampleData 
        ? getSampleValueByLanguage(metaTag.SampleValues, language)
        : '{{'+metaTag.Value+'}}'
      
      processedSubject = processedSubject.replace(tagPattern, replacementValue)
      processedContent = processedContent.replace(tagPattern, replacementValue)
      
      // Process SubMetaTags if they exist
      if (metaTag.SubMetaTags && metaTag.SubMetaTags.length > 0) {
        metaTag.SubMetaTags.forEach(subMetaTag => {
          const subTagPattern = new RegExp(`\\{\\{${subMetaTag.Key}\\}\\}`, 'g')
          const subReplacementValue = withSampleData 
            ? getSampleValueByLanguage(subMetaTag.SampleValues, language)
            : '{{'+subMetaTag.Value+'}}'
          
          processedSubject = processedSubject.replace(subTagPattern, subReplacementValue)
          processedContent = processedContent.replace(subTagPattern, subReplacementValue)
        })
      }
    })

  return {
    subject: processedSubject,
    content: processedContent
  }
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')

// Only a standalone occurrence of the literal is a token — "support-contact@gorillaworks.io" and
// "contact@gorillaworks.io.example" must not be rewritten, while a sentence-final "contact@gorillaworks.io." still is.
// The preceding character is CAPTURED (group 1) rather than checked with a lookbehind: lookbehind is a SyntaxError
// on Safari < 16.4, this regex is built at runtime in a render path, and nothing can transpile it away. The
// replacer re-emits the captured prefix. Kept in step with EmailConfigurationHelper.StandaloneDefaultSupportEmail
// (FBO.Services.Common), which uses a lookbehind because .NET supports it everywhere.
const standaloneDefaultSupportEmail = () =>
  new RegExp(`(^|[^A-Za-z0-9._%+\\-])${escapeRegExp(DEFAULT_SUPPORT_EMAIL)}(?![A-Za-z0-9\\-]|\\.[A-Za-z0-9])`, 'gi')

const resolveSupportEmail = (feedbackEmailAddress: string | null | undefined) =>
  escapeHtml(feedbackEmailAddress?.trim() || DEFAULT_SUPPORT_EMAIL)

/**
 * Renders the tenant support address into a base template the way the reminder processor does when it sends:
 * both the {{SupportEmail}} placeholder and a standalone literal default address become the tenant's
 * FeedbackEmailAddress. Without a tenant address the placeholder still resolves, to the default, so the preview
 * never shows it raw. A function replacer keeps '$' in the address from acting as a pattern, and the value is
 * escaped because it lands in dangerouslySetInnerHTML.
 */
export function applySupportEmail(html: string, feedbackEmailAddress: string | null | undefined): string {
  if (!html) return html

  const address = resolveSupportEmail(feedbackEmailAddress)

  return html
    .replace(standaloneDefaultSupportEmail(), (_match, prefix: string) => `${prefix}${address}`)
    .split(BaseTemplateTags.supportEmail).join(address)
}

/**
 * Placeholder-only pass for the merged document (after content is injected): the literal default in
 * tenant-authored content is left as written, but a {{SupportEmail}} typed there still renders as the address —
 * mirroring TemplateBuilderService.BuildReminderHtml, which would otherwise strip it as an unknown token.
 */
export function applySupportEmailPlaceholder(html: string, feedbackEmailAddress: string | null | undefined): string {
  if (!html) return html

  return html.split(BaseTemplateTags.supportEmail).join(resolveSupportEmail(feedbackEmailAddress))
}

export const generateTimeOptions = () => {
  const times = []
  for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 60) {
          const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
          const displayTime = new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
          })
          times.push({ value: timeString, label: displayTime })
      }
  }
  return times
}